import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SERVICES } from '../assets/constants';

const SERVICES_NAME = 'services';

/**
 * Manages embed mode: detects whether the app is embedded in another service via
 * postMessage and, if so, either navigates internally or forwards the login result
 * to the parent instead of navigating to a new URL.
 */
@Injectable()
export class EmbedLoginService {
  embedConfig: any = null;

  constructor(private router: Router) {}

  /**
   * Indicates whether the app is currently in embed mode.
   */
  get isEmbedded(): boolean {
    return !!this.embedConfig;
  }

  /**
   * Listens for `embedLogin` messages from the parent for the given sid and,
   * if received, activates embed mode, storing the message's origin and source.
   * @param sid Identifier of the service for which the embed message is expected
   */
  prepareEmbedMode(sid: string): void {
    try {
      // eslint-disable-next-line
      window.addEventListener('message', (ev) => {
        if (!ev.data || !ev.data.type || ev.data.type !== 'embedLogin') {
          return;
        }
        if (ev.data.data.serviceId === sid && SERVICES[ev.data.data.serviceId]) {
          // eslint-disable-next-line
          console.info('...starting embed login mode for', SERVICES[ev.data.data.serviceId]);
          this.embedConfig = {
            data: ev.data,
            origin: ev.origin,
            source: ev.source
          };
        }
      });
    } catch (error) {
      console.error('Unable to set embed mode:');
      console.error(error);
      this.embedConfig = null;
    }
  }

  /**
   * Resolves navigation after a successful login: if embedded and the destination is
   * another internal service, navigates within the SPA; if embedded but the
   * destination is an external service, forwards the result via postMessage; if not
   * embedded, navigates directly to the service's URL.
   * @param loginResponse Login response, containing the destination service's URL
   */
  onLoginSuccess(loginResponse: any): void {
    if (this.isEmbedded) {
      const serviceUrl = new URL(loginResponse.service);
      if (serviceUrl.host.toLowerCase().includes(SERVICES_NAME)) {
        const parameters = new URLSearchParams(serviceUrl.search);
        this.router.navigate([serviceUrl.pathname], {
          queryParams: Object.fromEntries(parameters.entries())
        });
      } else {
        this.postMessage(loginResponse);
      }
    } else {
      // eslint-disable-next-line
      window.location.replace(loginResponse.service);
    }
  }

  /**
   * Sends data to the embedding parent via `postMessage`, using the origin and
   * source captured when embed mode was established.
   * @param data Data to send to the parent
   */
  postMessage(data: object): void {
    this.embedConfig.source.postMessage({ type: 'embedLogin', data }, this.embedConfig.origin);
  }
}
